import { useEffect, useState } from "react";
import { Loader2, KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import M365Card from "../components/settings/M365Card";
import ApiKeysCard from "../components/settings/ApiKeysCard";

const inputCls = "w-full bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [salesperson, setSalesperson] = useState(user?.salesperson || "");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    api.get("/salespeople").then(({ data }) => setSalespeople(data)).catch(() => {});
  }, []);

  useEffect(() => { setSalesperson(user?.salesperson || ""); }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/me/profile", { salesperson });
      await refresh();
      toast.success("Salesperson profile updated");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[720px] mx-auto animate-fade-in">
        <h1 className="font-display font-black text-3xl tracking-tight mb-6">Settings</h1>

        <div className="rounded-xl border border-slate-800 bg-[#131C31] p-6 mb-6">
          <h2 className="font-display font-bold text-lg mb-4">Account</h2>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-slate-400">Name</dt><dd className="font-medium">{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Email</dt><dd className="font-medium">{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Role</dt><dd className="font-medium capitalize">{user?.role}</dd></div>
            <div className="flex justify-between items-center">
              <dt className="text-slate-400">MFA</dt>
              <dd className="inline-flex items-center gap-1 text-emerald-400"><ShieldCheck className="w-4 h-4" /> Enabled</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#131C31] p-6 mb-6">
          <h2 className="font-display font-bold text-lg mb-1 inline-flex items-center gap-2"><UserRound className="w-5 h-5 text-amber-400" /> Salesperson Profile</h2>
          <p className="text-xs text-slate-400 mb-4">Link your login to a salesperson so "My Reports" shows your personal performance.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className={labelCls}>I am</label>
              <select data-testid="settings-salesperson-select" className={inputCls} value={salesperson} onChange={(e) => setSalesperson(e.target.value)}>
                <option value="">Not set</option>
                {salespeople.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <button data-testid="save-salesperson-button" onClick={saveProfile} disabled={savingProfile} className="btn-primary rounded-lg px-5 py-2.5 inline-flex items-center gap-2 text-sm shrink-0">
              {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </div>

        {user?.role === "admin" && <><M365Card /><ApiKeysCard /></>}

        <div className="rounded-xl border border-slate-800 bg-[#131C31] p-6">
          <h2 className="font-display font-bold text-lg mb-4 inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" /> Change Password</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className={labelCls}>Current Password</label>
              <input data-testid="current-password-input" type="password" required className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>New Password (min 8 chars)</label>
              <input data-testid="new-password-input" type="password" required className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Confirm New Password</label>
              <input data-testid="confirm-password-input" type="password" required className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <button data-testid="change-password-button" type="submit" disabled={saving} className="btn-primary rounded-lg px-5 py-2.5 inline-flex items-center gap-2 text-sm">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Update Password
            </button>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
